import { FaTimes, FaChartBar, FaBed } from "react-icons/fa";
import { SaleItem } from "../../types/sales";
import BookedRoom from "../../models/BookedRoom";


type SalesDetailsProps = {
    isOpen: boolean;
    onClose: () => void;
    category: string; // Food, Drink, Laundry, Game, Room
    date: string;
    sale: SaleItem | BookedRoom;
};

export default function SalesDetailsModal({
    isOpen,
    onClose,
    category,
    date,
    sale,
}: SalesDetailsProps) {
    if (!isOpen) return null;
    const lower = category.toLowerCase();
    const isFoodOrDrink = lower === "foods" || lower === "drinks";
    const isLaundryOrGame = lower === "laundries" || lower === "games";
    const isRoom = lower === "rooms";

    // Type guards
    const isSaleItem = (s: SaleItem | BookedRoom): s is SaleItem =>
        (s as SaleItem).quantitySold !== undefined;
    const isRoomSale = (s: SaleItem | BookedRoom): s is BookedRoom =>
        (s as BookedRoom).roomNumber !== undefined;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center  p-4">
            <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 animate-fadeIn">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl"
                >
                    {FaTimes({})}
                </button>

                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <span className="text-blue-600 text-2xl">
                        {isRoom ? <span>{FaBed({})}</span> : <span>{FaChartBar({})}</span>}
                    </span>
                    <div>
                        <h2 className="capitalize text-xl font-bold text-gray-800">
                            {category} Sales Details
                        </h2>
                        <p className="text-sm text-gray-500">{date}</p>
                    </div>
                </div>
                
                {/* CONTENT */}
                <div className="space-y-3">
                    <p className="text-base font-semibold text-gray-900">
                        {isSaleItem(sale) ? sale.name : sale.guestName}
                    </p>

                    {/* FOOD / DRINK */}
                    {isFoodOrDrink && isSaleItem(sale) && (
                        <>
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { label: "Unit Price", value: `₦${(sale?.unitPrice ?? 0)?.toLocaleString()}`},
                                    { label: "Opening", value: sale.opening ?? 0 },
                                    { label: "Added", value: sale.stockIn ?? 0 },
                                    { label: "Spoilage", value: sale.spoilage ?? 0 },
                                    { label: "Sold", value: sale.quantitySold?? 0 },
                                    { label: "Closing", value: sale.closing ?? 0 },
                                    
                                ].map((s, i) => (
                                    <div
                                        key={i}
                                        className="bg-gray-50 p-3 rounded-lg text-center shadow-sm"
                                    >
                                        <p className="text-xs text-gray-500">{s.label}</p>
                                        <p className="font-semibold text-gray-800">{s.value}</p>
                                    </div>
                                ))}
                            </div>

                            {/* <div className="grid grid-cols-2 gap-3">
                                <div className="bg-gray-50 p-3 rounded-lg text-center shadow-sm">
                                    <p className="text-xs text-gray-500">Sold</p>
                                    <p className="font-semibold text-gray-800">
                                        {sale.quantitySold}
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-lg text-center shadow-sm">
                                    <p className="text-xs text-gray-500">Price</p>
                                    <p className="font-semibold text-gray-800">
                                        ₦{sale.unitPrice?.toLocaleString()}
                                    </p>
                                </div>
                            </div> */}
                        </>
                    )}

                    {/* GAME / LAUNDRY */}
                    {isLaundryOrGame && isSaleItem(sale) && (
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-gray-50 p-3 rounded-lg text-center shadow-sm">
                                <p className="text-xs text-gray-500">Sold</p>
                                <p className="font-semibold text-gray-800">
                                    {sale.quantitySold}
                                </p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg text-center shadow-sm">
                                <p className="text-xs text-gray-500">Price</p>
                                <p className="font-semibold text-gray-800">
                                    ₦{sale.unitPrice?.toLocaleString()}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ROOM */}
                    {isRoom && isRoomSale(sale) && (
                        <div className="space-y-3">
                            {/* Basic Info */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-gray-50 p-3 rounded-lg text-center shadow-sm">
                                    <p className="text-xs text-gray-500">Room No.</p>
                                    <p className="font-semibold text-gray-800">{sale.roomNumber}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-lg text-center shadow-sm">
                                    <p className="text-xs text-gray-500">Booking Type</p>
                                    <p className="font-semibold text-gray-800">{sale.bookingType}</p>
                                </div>
                            </div>

                            {/* Stay Duration */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-gray-50 p-3 rounded-lg text-center shadow-sm">
                                    <p className="text-xs text-gray-500">Check-In</p>
                                    <p className="font-semibold text-gray-800">{sale.checkInDate}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-lg text-center shadow-sm">
                                    <p className="text-xs text-gray-500">Check-Out</p>
                                    <p className="font-semibold text-gray-800">{sale.checkOutDate}</p>
                                </div>
                            </div>

                            {/* Financials */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-gray-50 p-3 rounded-lg text-center shadow-sm">
                                    <p className="text-xs text-gray-500">Room Rate</p>
                                    <p className="font-semibold text-gray-800">
                                        ₦{(sale.amount / sale.duration).toLocaleString()}
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-lg text-center shadow-sm">
                                    <p className="text-xs text-gray-500">Discount</p>
                                    <p className="font-semibold text-red-600">
                                        ₦{sale.discount.toLocaleString()}
                                    </p>
                                </div>
                            </div>

                            {/* Amounts */}
                            {/* <div className="bg-gray-50 p-3 rounded-lg flex justify-between items-center shadow-sm">
                                <p className="text-sm text-gray-600">Gross Amount</p>
                                <p className="text-sm font-semibold text-gray-800">
                                    ₦{sale.rate.toLocaleString()}
                                </p>
                            </div> */}

                            {sale.discount > 0 && (<div className="bg-blue-50 p-3 rounded-lg flex justify-between items-center shadow-sm">
                                <p className="text-sm text-blue-600 font-medium">Net Amount (After Discount)</p>
                                <p className="text-lg font-bold text-blue-700">
                                    ₦{(sale.amount - sale.discount).toLocaleString()}
                                </p>
                            </div>)}
                        </div>
                    )}


                    {/* TOTAL AMOUNT */}
                    <div className="bg-gray-100 p-3 rounded-lg flex justify-between items-center shadow-sm mt-2">
                        <p className="text-sm text-gray-600">Total Amount</p>
                        <p className="text-lg font-bold text-gray-900">
                            ₦{sale.amount.toLocaleString()}
                        </p>
                    </div>

                    {/* PAYMENT STATUS */}
                    <div
                        className={`p-3 rounded-lg text-center font-semibold ${sale?.paymentStatus?.toLowerCase() === "paid"
                            ? "bg-green-50 text-green-700"
                            : "bg-red-50 text-red-700"
                            }`}
                    >
                        {sale.paymentStatus}
                    </div>
                </div>

                {/* ACTIONS */}
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg transition"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
